import { lazy } from 'react';

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk'));

const RELOAD_KEY = 'chunk_reload_timestamp';
const RELOAD_COOLDOWN_MS = 10_000;

const forceReloadOnChunkError = (): void => {
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();

  if (lastReload && now - Number(lastReload) < RELOAD_COOLDOWN_MS) {
    return;
  }

  sessionStorage.setItem(RELOAD_KEY, String(now));
  window.location.reload();
};

export const retryImport = (
  fn: () => Promise<any>,
  retriesLeft = 3,
  interval = 500
): Promise<any> =>
  new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        if (retriesLeft === 1) {
          if (isChunkLoadError(error)) {
            forceReloadOnChunkError();
            return;
          }
          return reject(error);
        }
        setTimeout(() => {
          retryImport(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });

// Home
export const Home = lazy(() => retryImport(() => import('pages/home')));

// Campaigns
export const MyCampaignsPage = lazy(() =>
  retryImport(() => import('Components/Campaigns/MyCampaigns'))
);

export const CompanyCampaign = lazy(() =>
  retryImport(() => import('Components/CompanyCampaign'))
);
export const CreateCampaign = lazy(() =>
  retryImport(() => import('Components/Campaigns/CreateCampaign'))
);
export const Details = lazy(() =>
  retryImport(() => import('Components/Campaigns/Detail'))
);
export const FollowUp = lazy(() =>
  retryImport(() => import('Components/Campaigns/FollowUp'))
);

// Sales Enablement
export const Sales = lazy(() => retryImport(() => import('pages/sales')));

// Orders
export const OrderEnablement = lazy(() =>
  retryImport(() => import('pages/sales/modules/orders'))
);

export const Orders = lazy(() =>
  retryImport(() => import('pages/sales/modules/orders/subpages/orders'))
);

export const ThreePMReport = lazy(() =>
  retryImport(
    () => import('pages/sales/modules/orders/subpages/three-pm-report')
  )
);

export const OrderCreate = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/orders/subpages/order-create')
  )
);

export const OrderEmbeddedCreate = lazy(() =>
  retryImport(
    () => import('pages/sales/modules/orders/components/order-create/embedded')
  )
);

export const ReorderIntelligenceModule = lazy(() =>
  retryImport(
    () => import('pages/sales/modules/orders/subpages/reorder-intelligence')
  )
);

export const ReorderIntelligenceCustomerDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/reorder-intelligence/views/customers/subpages/customer-reorder-details')
  )
);

export const OrderDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/orders/subpages/order-details')
  )
);

export const Backorders = lazy(() =>
  retryImport(() => import('pages/sales/modules/orders/subpages/backorders'))
);

export const CustomerOrders = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/backorders/subpages/customer-orders')
  )
);

export const CustomerOrderItems = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/backorders/subpages/customer-orders/subpages/order-items')
  )
);

export const RecentlyOrderDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/recently-delivered/sub-pages/recently-order-details')
  )
);

export const Invoices = lazy(() =>
  retryImport(() => import('pages/sales/modules/orders/subpages/invoices'))
);

export const InvoiceDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/orders/subpages/invoices/subpages/invoice-details')
  )
);

// Quotes - Version-aware routers (V1/V2 based on feature flag)
export {
  QuoteListRouter as QuoteList,
  QuoteDetailsRouter as QuoteDetails,
  QuoteDetailsTabRouter as QuoteDetailsTab,
  QuoteCatalogRouter as QuoteDetailsCatalog,
  QuotePdfPreviewRouter as QuoteDetailsPdfPreview,
  QuoteReviewRouter as QuoteReview
} from 'pages/quotes/router';

export const Intelligence = lazy(() =>
  retryImport(() => import('pages/sales/modules/intelligence'))
);

export const IntelligenceDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/sales/modules/intelligence/subpages/intelligence-details')
  )
);

export const SingerAccountIntelligence = lazy(() =>
  retryImport(
    () => import('pages/sales/modules/intelligence/subpages/singer-account')
  )
);

// Auto RFQ
export const AutoRFQ = lazy(() =>
  retryImport(() => import('pages/auto-rfq/pages/list'))
);
export const AutoRFQDetails = lazy(() =>
  retryImport(() => import('pages/auto-rfq/pages/details'))
);

// Resources
export const Resources = lazy(() =>
  retryImport(() => import('Components/Resources'))
);
export const Favorites = lazy(() =>
  retryImport(() => import('Components/Favorites'))
);

// Workspaces
export const ContactsAndGroups = lazy(() =>
  retryImport(() => import('pages/contacts-and-groups'))
);
export const Contacts = lazy(() =>
  retryImport(() => import('pages/contacts-and-groups/contacts'))
);
export const Groups = lazy(() =>
  retryImport(() => import('pages/contacts-and-groups/groups'))
);
export const GroupDetail = lazy(() =>
  retryImport(() => import('pages/contacts-and-groups/group-detail'))
);
export const MyFiles = lazy(() =>
  retryImport(() => import('Components/Workspaces/MyFiles'))
);
export const MyPresentation = lazy(() =>
  retryImport(() => import('Components/Workspaces/MyPresentations'))
);
export const PresentationDetails = lazy(() =>
  retryImport(
    () =>
      import('Components/Workspaces/MyPresentations/Presentations/PresentationDetails')
  )
);
export const TemplateDetails = lazy(() =>
  retryImport(
    () =>
      import('Components/Workspaces/MyPresentations/Templates/TemplateDetails')
  )
);
export const WorkspacesSnippets = lazy(() =>
  retryImport(() => import('pages/workspaces/snippets'))
);
export const DigitalRooms = lazy(() =>
  retryImport(() => import('pages/digital-rooms'))
);

// Outbox
export const Outbox = lazy(() => retryImport(() => import('pages/outbox')));
export const OutlookDetails = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/outlook/outlook-details'))
);
export const OutlookViews = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/outlook/outlook-views'))
);
export const MultipleRecipientEmailDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/outbox/subpages/emails/email-details/multiple-recipient')
  )
);
export const SingleRecipientEmailDetails = lazy(() =>
  retryImport(
    () => import('pages/outbox/subpages/emails/email-details/single-recipient')
  )
);
export const Emails = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/emails'))
);
export const OutboxMyCampaigns = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/campaigns'))
);
export const Outlook = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/outlook'))
);
export const OutboxGetLink = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/get-links'))
);
export const OutboxQuoteShares = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/quote-shares'))
);
export const GetLinkDetails = lazy(() =>
  retryImport(() => import('pages/outbox/subpages/get-links/get-link-details'))
);
export const Overview = lazy(() =>
  retryImport(
    () =>
      import('pages/outbox/subpages/emails/email-details/multiple-recipient/subpages/overview')
  )
);
export const EmailDetails = lazy(() =>
  retryImport(
    () =>
      import('pages/outbox/subpages/emails/email-details/multiple-recipient/subpages/email-details')
  )
);
export const OutlookEmailDetails = lazy(() =>
  retryImport(
    () => import('pages/outbox/subpages/outlook/outlook-email-details')
  )
);

// Reporting
export const ThoughtspotLiveboard = lazy(() =>
  retryImport(() => import('pages/thoughtspot'))
);

export const TelaBioEmbed = lazy(() =>
  retryImport(() => import('pages/digital-rooms/pages/telabio-embed'))
);

// Admin Resources
export const EmailQueue = lazy(() =>
  retryImport(() => import('pages/email-queue'))
);
export const AdminResources = lazy(() =>
  retryImport(() => import('pages/admin/pages/resources'))
);
export const CreateCategory = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/resources/pages/category/pages/create')
  )
);
export const EditCategory = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/resources/pages/category/pages/edit')
  )
);
export const AddSingleResource = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/resources/pages/form/pages/add/single')
  )
);
export const EditSingleResource = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/resources/pages/form/pages/edit/single')
  )
);
export const AddBulkResource = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/resources/pages/form/pages/add/bulk')
  )
);
export const AddZipResource = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/resources/pages/form/pages/add/zip')
  )
);

// Admin Campaigns
export const AdminCampaigns = lazy(() =>
  retryImport(() => import('pages/admin/pages/campaigns'))
);
export const AdminCampaignDetails = lazy(() =>
  retryImport(() => import('pages/admin/pages/campaigns/pages/details'))
);
export const AdminCampaignDetailsContent = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/campaigns/pages/details/pages/main')
  )
);
export const AdminCampaignDetailsRecipients = lazy(() =>
  retryImport(
    () => import('pages/admin/pages/campaigns/pages/details/pages/recipients')
  )
);
export const AdminCampaignsTable = lazy(() =>
  retryImport(() => import('pages/admin/pages/campaigns/pages/table'))
);
export const CreateAdminCampaign = lazy(() =>
  retryImport(() => import('pages/admin/pages/campaigns/pages/create'))
);
export const UpdateAdminCampaign = lazy(() =>
  retryImport(() => import('pages/admin/pages/campaigns/pages/update'))
);

// Admin Users
export const AdminUsers = lazy(() =>
  retryImport(() => import('pages/admin/pages/users'))
);
export const AdminUsersHome = lazy(() =>
  retryImport(() => import('pages/admin/pages/users/pages/home'))
);
export const AdminUserCreate = lazy(() =>
  retryImport(() => import('pages/admin/pages/users/pages/create'))
);
export const AdminUserEdit = lazy(() =>
  retryImport(() => import('pages/admin/pages/users/pages/edit'))
);

// Admin AI Sales Enablement
export const AdminAISalesEnablement = lazy(() =>
  retryImport(() => import('pages/admin/pages/ai-sales-enablement'))
);

// Admin Home Screen(Banners)
export const HomeScreen = lazy(() =>
  retryImport(() => import('pages/admin/pages/home-screen'))
);
export const ManageHomeScreen = lazy(() =>
  retryImport(() => import('pages/admin/pages/home-screen/pages/home'))
);
export const AddBanner = lazy(() =>
  retryImport(
    () =>
      import('pages/admin/pages/home-screen/pages/home/pages/home-banners/pages/add')
  )
);
export const EditBanner = lazy(() =>
  retryImport(
    () =>
      import('pages/admin/pages/home-screen/pages/home/pages/home-banners/pages/edit')
  )
);
export const AddFeaturedResource = lazy(() =>
  retryImport(
    () =>
      import('pages/admin/pages/home-screen/pages/home/pages/featured-resources/pages/add')
  )
);
export const EditFeaturedResource = lazy(() =>
  retryImport(
    () =>
      import('pages/admin/pages/home-screen/pages/home/pages/featured-resources/pages/edit')
  )
);

// Settings
export const Settings = lazy(() => retryImport(() => import('pages/settings')));

// Authentication
export const Authenticate = lazy(() =>
  retryImport(() => import('pages/login'))
);

// Products
export const Products = lazy(() => retryImport(() => import('pages/products')));
export const ProductDetailsPage = lazy(() =>
  retryImport(() => import('pages/products/product-details-page'))
);
export const CustomerViewPage = lazy(() =>
  retryImport(() => import('pages/products/customer-view-page'))
);

export const OutlookCatalog = lazy(() =>
  retryImport(() => import('pages/outlook/catalog'))
);

// Email Verification
export const EmailVerification = lazy(() =>
  retryImport(() => import('Components/EmailVerify'))
);

// SSO Callback
export const SSOCallback = lazy(() =>
  retryImport(() => import('pages/sso-callback'))
);
